import React, { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, PlayCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function AnimatedCounter({
  value,
  suffix,
  inView
}: {
  value: number;
  suffix: string;
  inView: boolean;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => Math.round(latest).toString());
  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, {
        duration: 2,
        ease: [0.16, 1, 0.3, 1]
      });
      return controls.stop;
    }
  }, [inView, value]);
  return <span className="flex items-baseline" data-id="element-198">
      <motion.span data-id="element-199">{rounded}</motion.span>
      <span data-id="element-200">{suffix}</span>
    </span>;
}
const fadeUp = (delay = 0) => ({
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.5,
    delay,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
});
export function Hero() {
  const {
    t
  } = useLanguage();
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, {
    once: true,
    margin: '-20px'
  });
  const stats = [{
    value: 50,
    suffix: '+',
    label: t('hero.stat.companies'),
    emoji: '🏢'
  }, {
    value: 12,
    suffix: '',
    label: t('hero.stat.countries'),
    emoji: '🌍'
  }, {
    value: 30,
    suffix: '+',
    label: t('hero.stat.projects'),
    emoji: '🚀'
  }, {
    value: 100,
    suffix: '+',
    label: t('hero.stat.events'),
    emoji: '🎯'
  }];
  return <section className="relative w-full overflow-hidden px-4 pt-24 pb-6 md:pt-32 md:pb-8 flex flex-col" style={{
    backgroundColor: 'var(--bg-primary)',
    minHeight: '100vh'
  }} data-id="element-201">
      {/* Subtle background accent */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none" data-id="element-202">
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
      }} data-id="element-203" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl flex-1 flex flex-col md:flex-row md:items-center md:justify-between md:gap-10" data-id="element-204">
        {/* ===== MOBILE LAYOUT: Title → Image → Desc → Buttons ===== */}
        {/* ===== DESKTOP LAYOUT: Left(title+desc+buttons) | Right(image) ===== */}

        {/* Left column (desktop) */}
        <div className="w-full md:w-[55%] flex flex-col items-start text-left" data-id="element-205">
          {/* Title — always first */}
          <motion.h1 {...fadeUp(0.05)} className="mb-5 text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight" style={{
          color: 'var(--text-primary)'
        }} data-id="element-206">
            {t('hero.title')}
            <br data-id="element-207" />
            <span style={{
            color: 'var(--text-primary)'
          }} data-id="element-208">
              {t('hero.subtitle')}
            </span>
          </motion.h1>

          {/* Image — mobile only, between title and description */}
          <motion.div {...fadeUp(0.1)} className="w-full aspect-video rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 mb-5 md:hidden" style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-secondary)'
        }} data-id="element-209">
            <PlayCircleIcon className="w-12 h-12 opacity-50" style={{
            color: 'var(--text-muted)'
          }} data-id="element-210" />
            <span className="text-sm font-medium opacity-50" style={{
            color: 'var(--text-muted)'
          }} data-id="element-211">
              {t('hero.placeholder')}
            </span>
          </motion.div>

          {/* Description */}
          <motion.p {...fadeUp(0.15)} className="mb-6 max-w-xl text-base md:text-xl" style={{
          color: 'var(--text-secondary)',
          lineHeight: '1.7'
        }} data-id="element-212">
            {t('hero.desc')}
          </motion.p>

          {/* Buttons */}
          <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-213">
            <a href="#about" className="flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-medium border transition-colors" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)'
          }} data-id="element-214">
              {t('hero.cta.more')}
            </a>
            <a href="#join" className="group flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-medium transition-colors" style={{
            backgroundColor: 'var(--accent)',
            color: '#FFFFFF'
          }} data-id="element-215">
              {t('hero.cta.join')}
              <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-216" />
            </a>
          </motion.div>
        </div>

        {/* Right Side: Image placeholder — desktop only */}
        <motion.div {...fadeUp(0.1)} className="hidden md:flex w-[45%] aspect-square lg:aspect-[4/3] rounded-3xl border-2 border-dashed flex-col items-center justify-center gap-4" style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-secondary)'
      }} data-id="element-217">
          <PlayCircleIcon className="w-16 h-16 opacity-50" style={{
          color: 'var(--text-muted)'
        }} data-id="element-218" />
          <span className="text-lg font-medium opacity-50" style={{
          color: 'var(--text-muted)'
        }} data-id="element-219">
            {t('hero.placeholder')}
          </span>
        </motion.div>
      </div>

      {/* Stats */}
      <div ref={statsRef} className="relative z-10 mx-auto max-w-7xl w-full pt-8 pb-2" data-id="element-220">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8" data-id="element-221">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 12
        }} animate={isStatsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.1 + index * 0.08,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="flex items-center gap-3" data-id="element-222">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0" style={{
            backgroundColor: 'var(--bg-tertiary)'
          }} data-id="element-223">
                {stat.emoji}
              </div>
              <div className="flex flex-col" data-id="element-224">
                <div className="text-xl sm:text-3xl font-bold" style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }} data-id="element-225">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={isStatsInView} data-id="element-226" />
                </div>
                <div className="text-[10px] sm:text-sm uppercase tracking-wider font-medium" style={{
              color: 'var(--text-muted)'
            }} data-id="element-227">
                  {stat.label}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}